import { useCallback, useEffect, useSyncExternalStore } from "react";

/**
 * The till's light/dark preference.
 *
 * The redesign puts a theme toggle in the POS top bar. The till opens LIGHT —
 * PAPER's till, which every look wears — and the person holding it may switch
 * it to the dark night room, which is its own setting and independent of the
 * dashboard look. `docs/design/layout-typography-color.md` records the default
 * and this override together.
 *
 * Persisted per device, not per user: a till is a shared device, and the
 * preference belongs to the screen in the room rather than to whoever is
 * logged into it. The key matches the mock's own (`triotyPosTheme`).
 *
 * An explicit choice — either way — always wins; with none, the till is light.
 *
 * A tiny external store, the same shape as `useDashboardTheme`, so a page can
 * READ the till's scope (`usePosThemeChoice`) without owning the body class.
 */
export type PosTheme = "dark" | "light";

const STORAGE_KEY = "triotyPosTheme";
const DEFAULT_THEME: PosTheme = "light";

function readStored(): PosTheme | null {
  // Wrapped: a private window, cleared site data, or a browser set to block
  // storage all throw on access rather than returning null.
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null;
  }
}

let stored: PosTheme | null = readStored();
const listeners = new Set<() => void>();

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getStored(): PosTheme | null {
  return stored;
}

/** The till's scope: the stored choice, else light. Read-only. */
export function usePosThemeChoice(): PosTheme {
  const choice = useSyncExternalStore(subscribe, getStored, getStored);
  return choice ?? DEFAULT_THEME;
}

/**
 * Chooses the till's scope for this device (or, with `null`, forgets the choice
 * so the default applies again — what a spec resets to).
 */
export function setPosTheme(next: PosTheme | null): void {
  stored = next;
  try {
    if (next === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // A till that cannot persist the choice still honours it for this
    // session; losing the preference is not worth failing the tap.
  }
  listeners.forEach((listener) => listener());
}

export function usePosTheme(): {
  theme: PosTheme;
  toggle: () => void;
} {
  const theme = usePosThemeChoice();

  const toggle = useCallback(() => {
    setPosTheme(theme === "dark" ? "light" : "dark");
  }, [theme]);

  /**
   * The scope class goes on `<body>`, not only on the screen's own element.
   *
   * `ModalSheet` — which every `Modal` and dialog in the app renders through —
   * portals to `document.body`. A scope class living only on the screen's div
   * therefore does NOT reach it, so every dialog opened from the dark till
   * painted itself from the LIGHT platform tokens: a white modal over a black
   * floor. Putting the class on the body means the app tree and every portal
   * read the same scope.
   */
  useEffect(() => {
    const { body } = document;
    if (theme === "dark") {
      body.classList.add("pos-dark");
    } else {
      body.classList.remove("pos-dark");
    }
    return () => body.classList.remove("pos-dark");
  }, [theme]);

  return { theme, toggle };
}

/**
 * Whether the till draws PAPER's till markup: always, light or dark.
 *
 * Dark used to switch the look off and fall back to the till's old tiles; since
 * 2026-10-26 (founder: "dark must behave exactly like light, only the colours
 * change") the PAPER stylesheets apply under `.pos-dark` with a dark palette,
 * so the till is the same screen in both. Kept as a hook so the screens that
 * still branch on it read one answer; the old branches are now unreachable.
 */
export function usePaperTill(): boolean {
  return true;
}
